import React from 'react';
import { AlertCircle, AlertTriangle, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { MetaTokenStatus } from '@/lib/api/interfaces/integrations.interface';

interface IntegrationWarningProps {
  tokenStatus?: MetaTokenStatus;
}

export const IntegrationWarning: React.FC<IntegrationWarningProps> = ({ tokenStatus }) => {
  if (tokenStatus === 'expired') {
    return (
      <div className="bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
            <AlertCircle className="h-4 w-4 text-red-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-semibold text-red-900 mb-1">
              Meta-Verbindung abgelaufen
            </h4>
            <p className="text-sm text-red-700 leading-relaxed mb-3">
              Deine Verbindung zu Facebook/Instagram ist abgelaufen. Bitte verbinde dein Konto erneut, um wieder posten zu können.
            </p>
            <Link
              to="/integrations"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-medium rounded-lg transition-colors"
            >
              <RefreshCw className="h-3 w-3" />
              Jetzt neu verbinden
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (tokenStatus === 'expiring_soon') {
    return (
      <div className="bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center">
            <AlertTriangle className="h-4 w-4 text-yellow-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-semibold text-yellow-900 mb-1">
              Meta-Verbindung läuft bald ab
            </h4>
            <p className="text-sm text-yellow-800 leading-relaxed mb-2">
              Deine Verbindung zu Facebook/Instagram läuft in weniger als 7 Tagen ab.{' '}
              <Link to="/integrations" className="underline font-medium">
                Jetzt erneuern
              </Link>
              , um Unterbrechungen zu vermeiden.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // not_connected fallback
  return (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-lg p-4">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
          <AlertCircle className="h-4 w-4 text-blue-600" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-blue-900 mb-1">Keine Accounts verbunden</h4>
          <p className="text-sm text-blue-700 leading-relaxed">
            Verbinden Sie Ihre Social Media Accounts unter{' '}
            <Link to="/integrations" className="underline">Integrationen</Link>, um zu posten.
          </p>
        </div>
      </div>
    </div>
  );
};
